import { createDemoStore } from '@/demo/core/create-demo-store';
import { getPerson } from '../../data/company';
import { STEP_ORDER } from './onboarding-types';
import type { OnboardingCase } from './onboarding-types';
import type { InviteInput } from './onboarding-cases';
import { buildInvitedCase, buildSeedCases } from './onboarding-cases';
import {
  KESTREL_RISK_TIER,
  KESTREL_SUPPLIER_MASTER_ID,
  PROCUREMENT_APPROVER,
} from './onboarding-kestrel';

interface OnboardingState {
  /** Newest invite first, then the seeded cases in queue order. */
  cases: OnboardingCase[];
}

function buildSeed(): OnboardingState {
  return { cases: buildSeedCases() };
}

export const onboardingStore = createDemoStore<OnboardingState>(buildSeed);

const CASE_NUMBER = /^ONB-(\d+)$/;

/** The next free case id, such as 'ONB-0094'. */
export function nextCaseId(cases: OnboardingCase[]): string {
  let highest = 0;
  for (const item of cases) {
    const match = CASE_NUMBER.exec(item.id);
    if (match) highest = Math.max(highest, Number(match[1]));
  }
  return `ONB-${String(highest + 1).padStart(4, '0')}`;
}

export function getCase(
  cases: OnboardingCase[],
  caseId: string,
): OnboardingCase | undefined {
  return cases.find((item) => item.id === caseId);
}

/** Add a case for a newly invited supplier and return its id. */
export function inviteSupplier(input: Omit<InviteInput, 'id'>): string {
  const id = nextCaseId(onboardingStore.getState().cases);
  onboardingStore.update((draft) => {
    draft.cases.unshift(buildInvitedCase({ ...input, id }));
  });
  return id;
}

export function counterDeviation(caseId: string, deviationId: string): void {
  onboardingStore.update((draft) => {
    const target = getCase(draft.cases, caseId);
    const deviation = target?.steps.agreements.content.deviations?.find(
      (item) => item.id === deviationId,
    );
    if (deviation) deviation.state = 'countered';
  });
}

/** True while the case waits on the Procurement approval. */
export function hasPendingProcurementApproval(target: OnboardingCase): boolean {
  return (
    target.steps.approvals.content.approvals?.some(
      (item) => item.role === 'Procurement' && item.status === 'pending',
    ) ?? false
  );
}

/**
 * Procurement approves: the supplier is created in the ERP, every step
 * closes, progress reaches 100% and the oversight level is assigned.
 */
export function approveProcurement(caseId: string): void {
  onboardingStore.update((draft) => {
    const target = getCase(draft.cases, caseId);
    if (!target || !hasPendingProcurementApproval(target)) return;
    const procurement = target.steps.approvals.content.approvals?.find(
      (item) => item.role === 'Procurement',
    );
    if (procurement) {
      procurement.status = 'approved';
      procurement.decidedDaysAgo = 0;
      procurement.note = `Approved. Supplier ${KESTREL_SUPPLIER_MASTER_ID} created and synced to the ERP.`;
    }
    for (const key of STEP_ORDER) {
      if (target.steps[key].status !== 'na') target.steps[key].status = 'done';
    }
    target.steps.approvals.content.summary =
      'All four approvals are complete. The supplier is created in the ERP and the portal is enabled.';
    target.stage = 'Completed';
    target.progress = 100;
    target.waitingOn = 'None';
    target.waitingDetail = undefined;
    target.completedDaysAgo = 0;
    target.supplierMasterId = KESTREL_SUPPLIER_MASTER_ID;
    target.paymentTerms = 'Net 60';
    target.riskTier = KESTREL_RISK_TIER;
    target.aiSummary = {
      title: `Onboarding complete: supplier ${KESTREL_SUPPLIER_MASTER_ID} is active`,
      body: 'The supplier record is created and synced to the ERP with Net 60 terms, and portal access is enabled. The on-site audit and sample unit test KD-VFD-26-019 continue as post-onboarding conditions before volume is released through RFQ-2026-022.',
      tone: 'success',
    };
    target.agentLog.unshift(
      {
        minutesAgo: 0,
        text: `Created supplier ${KESTREL_SUPPLIER_MASTER_ID} and synced it to the ERP`,
        detail: 'Payment terms Net 60. Supplier portal access enabled.',
      },
      {
        minutesAgo: 1,
        text: `${getPerson(PROCUREMENT_APPROVER).name} approved the case`,
        detail: 'All four approvals complete.',
      },
    );
  });
}

export function resetOnboardingDemo(): void {
  onboardingStore.reset();
}
